import React, { useState, useEffect, useRef, useMemo } from 'react';
import styles from './SearchPage.module.css';
import { FiSearch, FiX } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { formatSymbol } from '../../utils/symbolFormatter';
import * as api from '../../utils/api';

const getExchange = (symbol) => {
  if (symbol.symbol_type === 'F&O') {
    const userSymbol = symbol.user_symbol.toUpperCase();
    if (userSymbol.startsWith('NIFTY') || userSymbol.startsWith('BANKNIFTY')) {
      return 'NSE';
    } else if (userSymbol.startsWith('SENSEX') || userSymbol.startsWith('BANKEX')) {
      return 'BSE';
    }
  }
  return 'NSE';
};

const SearchPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [allSymbols, setAllSymbols] = useState([]);
  const [searchHistory, setSearchHistory] = useState(() => {
    try {
      const stored = localStorage.getItem('searchHistory');
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });
  
  const navigate = useNavigate();
  const searchInputRef = useRef(null);

  // 1. Fetch all symbols once
  useEffect(() => {
    const fetchSymbols = async () => {
      try {
        const { data } = await api.getSymbols();
        const symbols = data.filter(Boolean);
        setAllSymbols(symbols);
      } catch (error) {
        console.error("Error fetching symbols in SearchPage:", error);
      }
    };
    fetchSymbols();
  }, []);

  // 2. Client-side filtering
  const filteredSearchResults = useMemo(() => {
    if (!searchTerm.trim()) return [];
    const term = searchTerm.toLowerCase();
    return allSymbols.filter(s =>
      s.user_symbol.toLowerCase().includes(term)
    ).slice(0, 20);
  }, [searchTerm, allSymbols]);

  // 3. Persist History
  useEffect(() => {
    localStorage.setItem('searchHistory', JSON.stringify(searchHistory));
  }, [searchHistory]);

  const handleSymbolClick = (symbolName) => {
    setSearchHistory(prev => {
      const filtered = prev.filter(h => h !== symbolName);
      return [symbolName, ...filtered].slice(0, 10);
    });
    navigate(`/symbol/${symbolName}`);
  };

  return (
    <div className={styles['search-page-container']}>
      <div className={styles['search-header']}>
        <div className={styles['search-row']}>
            <div className={styles['search-input-wrapper']}>
              <FiSearch className={styles['search-icon']} />
              <input
                type="text"
                placeholder="Search stocks, options..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                ref={searchInputRef}
                className={styles['search-input']}
                autoFocus
                autoComplete="off"
              />
              {searchTerm && (
                <button className={styles['clear-button']} onClick={() => setSearchTerm('')}>
                  <FiX />
                </button>
              )}
            </div>
        </div>
      </div>

      <div className={styles['content-area']}>
        {searchTerm ? (
          filteredSearchResults.length > 0 ? (
            <ul className={styles['results-list']}>
              {filteredSearchResults.map(s => (
                <li key={`${s.user_symbol}-${s.sr_no}`} className={styles['result-item']} onClick={() => handleSymbolClick(s.user_symbol)}>
                  <div className={styles['res-main']}>
                    <span className={styles['res-symbol']}>{formatSymbol(s.user_symbol)}</span>
                  </div>
                  <div className={styles['res-meta']}>
                    <span className={styles['res-type']}>{s.symbol_type}</span>
                    <span className={styles['res-exch']}>{getExchange(s)}</span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className={styles['status-msg']}>No matches found for "{searchTerm}"</p>
          )
        ) : (
          <>
            {searchHistory.length > 0 && (
              <div className={styles['section']}>
                <h3 className={styles['section-title']}>Recent Searches</h3>
                <ul className={styles['history-list']}>
                  {searchHistory.map((s) => (
                    <li key={`${s}-hist`} className={styles['history-item']} onClick={() => handleSymbolClick(s)}>
                      <div className={styles['hist-info']}>
                        <span className={styles['hist-symbol']}>{formatSymbol(s)}</span>
                      </div>
                      <button className={styles['remove-hist']} onClick={(e) => { e.stopPropagation(); setSearchHistory(prev => prev.filter(h => h !== s)); }}>
                        <FiX />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SearchPage;
